/* eslint-disable no-console */
/* eslint-disable no-mixed-operators */
/* eslint-disable max-len */
import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import EmbedVideos, { type EmbedVideosRef } from './components/EmbedVideos';
import EmbedImages, { type EmbedImagesRef } from './components/EmbedImages';
import EmbedDocuments, { type EmbedDocumentsRef } from './components/EmbedDocuments';
import EmbedPodcasts, { type EmbedPodcastsRef } from './components/EmbedPodcasts';
import { hasMultipleUrls, isValidHttpUrl, estimateDataUriSize } from './EmbedHelpers';
import { ERROR_MESSAGES, MAX_DATA_URI_SIZE } from './types';
import type { MediaUrlProcessorProps, UploadResult } from './types';

const MediaUrlProcessor = ({
	Embednote,
	fileType,
	onFileSelect,
	onClose,
	companyId: propCompanyId,
	fromCreation
}: MediaUrlProcessorProps): React.ReactElement =>
{
	const [ url, setUrl ] = useState<string>('');
	const [ isProcessing, setIsProcessing ] = useState(false);
	const [ error, setError ] = useState<string | null>(null);
	const [ imagePreview, setImagePreview ] = useState<string | null>(null);

	// Get userData from Redux
	const userData = useSelector((state: any) => state?.mysession?.session);
	const companyId = propCompanyId || userData?.companyId;

	// Refs for child components
	const videoRef = useRef<EmbedVideosRef>(null);
	const imageRef = useRef<EmbedImagesRef>(null);
	const documentRef = useRef<EmbedDocumentsRef>(null);
	const podcastRef = useRef<EmbedPodcastsRef>(null);

	// Helper function to get placeholder text based on file type
	const getPlaceholderText = (type: string): string =>
	{
		const placeholders = {
			video    : 'Enter YouTube URL',
			image    : 'Enter Image URL (.jpg, .png, etc.) or paste data URI',
			document : 'Enter Document URL with extension (.pdf, .docx, etc.)',
			podcast  : 'Enter MP3 URL (.mp3)',
			default  : 'Enter URL'
		};

		return placeholders[type as keyof typeof placeholders] || placeholders.default;
	};

	// Common URL validation
	const validateCommonUrl = (inputUrl: string): boolean =>
	{
		try
		{
			// Enhanced detection for multiple URLs - more comprehensive patterns
			if (hasMultipleUrls(inputUrl))
			{
				setError(ERROR_MESSAGES.MULTIPLE_URLS);

				return false;
			}

			// Special handling for data URIs
			if (inputUrl.startsWith('data:'))
			{
				// For images, validate that it's an image data URI
				if (fileType === 'image')
				{
					if (!inputUrl.startsWith('data:image/'))
					{
						setError('Data URL must be an image type');

						return false;
					}

					// Check data URI size
					const size = estimateDataUriSize(inputUrl);

					if (size > MAX_DATA_URI_SIZE)
					{
						const sizeMB = (size / (1024 * 1024)).toFixed(2);

						setError(`Image too large (${sizeMB} MB). Maximum size is 5MB.`);

						return false;
					}

					// For image data URIs, always accept them regardless of format
					// The browser knows how to handle them
					return true;
				}
				else if (fileType === 'document')
				{
					// We don't support document data URIs, only URLs with extensions
					setError('For documents, please use a URL with file extension (.pdf, .docx, etc.)');

					return false;
				}
				else if (fileType === 'video')
				{
					// We don't support video data URIs currently
					setError('Data URLs are only supported for images');

					return false;
				}

				// For other file types
				return true;
			}

			// Quick validation of essential URL format first - must have http(s) protocol
			if (!isValidHttpUrl(inputUrl))
			{
				setError(ERROR_MESSAGES.INVALID_URL);

				return false;
			}

			return true;
		}
		catch (err)
		{
			console.error('URL validation error:', err);
			setError(ERROR_MESSAGES.INVALID_URL);

			return false;
		}
	};

	// Handle upload completion from child components
	const handleUploadComplete = (result: UploadResult) =>
	{
		// Notify parent and close
		if (onFileSelect)
		{
			// For documents, only call the parent callback if we didn't already upload directly
			if (fileType === 'document' && result.skipParentCallback)
			{
				console.log('Skipping parent callback for document upload to prevent duplicate API calls');
			}
			// For podcasts, only call the parent callback if we didn't already upload directly
			else if (fileType === 'podcast' && result.skipParentCallback)
			{
				console.log('Skipping parent callback for podcast upload to prevent duplicate API calls');
			}
			else if (fileType === 'video' && result.videoId)
			{
				onFileSelect(result.url, result.source, result.videoId);
			}
			else if (fileType === 'podcast')
			{
				// For podcasts, pass the URL as first param and the entire result object as key (JSON string)
				// This ensures FileUploadHandler receives the processed audio payload
				onFileSelect(result.url, result.source, JSON.stringify(result));
			}
			else
			{
				onFileSelect(result.url, result.source);
			}
		}

		if (onClose) onClose();
	};

	// Handle errors from child components
	const handleError = (errorMessage: string) =>
	{
		setError(errorMessage);
	};

	// Process and upload in a single step
	const handleAddUrl = async () =>
	{
		if (isProcessing || !url.trim()) return;

		// Add global check for ongoing uploads by other users (this would need a state manager like Redux)
		// This is a hypothetical check - you'll need to implement actual logic to track uploads
		const isAnotherUploadInProgress = false; // Replace with actual check

		if (isAnotherUploadInProgress)
		{
			setError(ERROR_MESSAGES.IN_PROGRESS);

			return;
		}

		setIsProcessing(true);
		setError(null);

		try
		{
			// 1. Common validation
			if (!validateCommonUrl(url))
			{
				setIsProcessing(false);

				return;
			}

			// 2. Route to appropriate child component for processing
			const processors = {
				video    : () => videoRef.current?.process(),
				image    : () => imageRef.current?.process(),
				document : () => documentRef.current?.process(),
				podcast  : () => podcastRef.current?.process()
			};

			console.log('🔍 Routing to processor for fileType:', fileType);

			const processor = processors[fileType as keyof typeof processors];

			if (processor)
			{
				console.log('✅ Found processor for', fileType, 'calling process()');
				await processor();
			}
			else
			{
				console.log('❌ No processor found for fileType:', fileType);
				throw new Error(`Unsupported file type: ${fileType}`);
			}
		}
		catch (processingError)
		{
			console.error('Error processing URL:', processingError);
			toast.error('Failed to process URL. Please try again.');
			setError('Failed to process URL. Please try again.');
		}
		finally
		{
			setIsProcessing(false);
		}
	};

	const LoadingOverlay = () => (
		<Box
			sx={{
				position        : 'absolute',
				top             : 0,
				left            : 0,
				right           : 0,
				bottom          : 0,
				display         : 'flex',
				alignItems      : 'center',
				justifyContent  : 'center',
				backgroundColor : 'rgba(255, 255, 255, 0.7)',
				zIndex          : 1
			}}
		>
			<CircularProgress />
		</Box>
	);

	const ImagePreview = () => (
		<Box sx={{ mb: 2, maxWidth: '100%', maxHeight: '150px', overflow: 'hidden' }}>
			<img
				src={imagePreview || ''}
				alt='Image Preview'
				style={{
					maxWidth     : '100%',
					maxHeight    : '150px',
					objectFit    : 'contain',
					border       : '1px solid #eee',
					borderRadius : '4px'
				}}
			/>
			<Typography variant='caption' sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
				Data URI Image Preview
			</Typography>
		</Box>
	);

	const DefaultUploadIcon = () => (
		<Box component='img'
			src='/images/Upload.svg'
			sx={{ height: '150px', width: '200px', objectFit: 'contain' }}
			alt='Upload'
		/>
	);

	const ProcessingButton = () => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<CircularProgress size={20} color='inherit' />
			Processing...
		</Box>
	);

	const HelpText = () =>
	{
		const helpTexts = {
			image    : 'Supports regular image URLs and image data URIs (max 5MB)',
			document : 'Supports document URLs with file extensions (.pdf, .docx, etc.)',
			podcast  : 'Supports MP3 audio URLs (.mp3 format only)'
		};

		const text = helpTexts[fileType as keyof typeof helpTexts];

		return text
			? (
				<Typography variant='caption' sx={{ mt: 1, color: '#666', textAlign: 'center' }}>
					{text}
				</Typography>
			)
			: null;
	};

	return (
		<Box
			sx={{
				display        : 'flex',
				flexDirection  : 'column',
				alignItems     : 'center',
				justifyContent : 'center',
				width          : '70%',
				position       : 'relative'
			}}
		>
			{isProcessing && <LoadingOverlay />}

			<Box sx={{
				border         : '1px dashed #ccc',
				display        : 'flex',
				flexDirection  : 'column',
				alignItems     : 'center',
				justifyContent : 'center',
				paddingX       : 8,
				paddingY       : 4,
				borderRadius   : 2,
				width          : '100%',
				position       : 'relative'
			}}
			>
				{imagePreview
					? <ImagePreview />
					: <DefaultUploadIcon />
				}

				<TextField
					placeholder={getPlaceholderText(fileType)}
					variant='outlined'
					size='small'
					fullWidth
					sx={{ mt: 2 }}
					value={url}
					onChange={(e) =>
					{
						setUrl(e.target.value);
						if (error) setError(null);
					}}
					error={Boolean(error)}
					helperText={error}
					disabled={isProcessing}
					inputProps={{
						'aria-label'  : 'URL input field',
						'data-testid' : 'url-input'
					}}
				/>

				<Button
					variant='contained'
					fullWidth
					sx={{
						color     : '#fff',
						marginTop : '16px',
						bgcolor   : '#24a4c4 !important',
						'&:hover' : {
							bgcolor : '#1e8ca8 !important'
						},
						'&:disabled' : {
							bgcolor : '#a0d2dd !important',
							color   : '#f5f5f5'
						}
					}}
					onClick={handleAddUrl}
					disabled={!url.trim() || isProcessing}
					data-testid='add-url-button'
				>
					{isProcessing ? <ProcessingButton /> : 'Add URL'}
				</Button>

				<HelpText />
			</Box>

			<Typography
				variant='caption'
				sx={{ marginTop: 2, color: '#949494' }}
				fontSize={14}
				fontWeight={600}
			>
				{Embednote}
			</Typography>

			{/* Child Components - Hidden but functional */}
			{fileType === 'video' && (
				<EmbedVideos
					ref={videoRef}
					url={url}
					isProcessing={isProcessing}
					onUploadComplete={handleUploadComplete}
					onError={handleError}
					companyId={companyId || ''}
				/>
			)}

			{fileType === 'image' && (
				<EmbedImages
					ref={imageRef}
					url={url}
					isProcessing={isProcessing}
					imagePreview={imagePreview}
					setImagePreview={setImagePreview}
					onUploadComplete={handleUploadComplete}
					onError={handleError}
					companyId={companyId || ''}
					fromCreation={fromCreation}
				/>
			)}

			{fileType === 'document' && (
				<EmbedDocuments
					ref={documentRef}
					url={url}
					isProcessing={isProcessing}
					onUploadComplete={handleUploadComplete}
					onError={handleError}
					companyId={companyId || ''}
				/>
			)}

			{fileType === 'podcast' && (
				<EmbedPodcasts
					ref={podcastRef}
					url={url}
					isProcessing={isProcessing}
					onUploadComplete={handleUploadComplete}
					onError={handleError}
					companyId={companyId || ''}
				/>
			)}
		</Box>
	);
};

export default MediaUrlProcessor;